import React, { useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { format } from 'date-fns'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    InteractionMode
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import zoomPlugin from 'chartjs-plugin-zoom'
import 'chartjs-adapter-date-fns'

// Chart.js 등록
ChartJS.register(
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    zoomPlugin
)

// 생체신호 데이터 인터페이스
export interface IBiometricChartItem {
    x: number
    y: number
    yMin?: number
    yMax?: number
}

interface IProps {
    className?: string
    heartRateData?: IBiometricChartItem[]
    respirationData?: IBiometricChartItem[]
    stepData?: IBiometricChartItem[]
    fallData?: IBiometricChartItem[]
    realtimeHeartRate?: IBiometricChartItem[]
    realtimeRespiration?: IBiometricChartItem[]
    realtimeSteps?: IBiometricChartItem[]
    realtimeFalls?: IBiometricChartItem[]
}

const BiometricDashboard: React.FC<IProps> = ({
    className,
    heartRateData,
    respirationData,
    stepData,
    fallData,
    realtimeHeartRate,
    realtimeRespiration,
    realtimeSteps,
    realtimeFalls
}) => {
    const { t } = useTranslation();
    // 차트 참조들
    const heartRateChartRef = useRef<ChartJS<'line'>>(null)
    const respirationChartRef = useRef<ChartJS<'line'>>(null)
    const stepChartRef = useRef<ChartJS<'line'>>(null)
    const fallChartRef = useRef<ChartJS<'line'>>(null)

    // 데이터 모드 상태
    const [viewMode, setViewMode] = useState<'daily' | 'realtime'>('daily')
    
    // 줌/팬 상태 (모든 차트 동기화용)
    const [syncedZoom, setSyncedZoom] = useState<{
        min?: number
        max?: number
    }>({})

    // 데이터 선택 및 변환 로직
    const processedData = useMemo(() => {
        const selectData = (dailyData?: IBiometricChartItem[], realtimeData?: IBiometricChartItem[]) => {
            const sourceData = viewMode === 'realtime' ? (realtimeData || []) : (dailyData || [])
            return sourceData.filter(item => 
                !isNaN(item.x) && item.x > 0 && 
                item.y != null && !isNaN(item.y)
            ).sort((a, b) => a.x - b.x).map(item => ({
                x: item.x * 1000, // Chart.js는 밀리초 단위
                y: item.y,
                yMin: item.yMin,
                yMax: item.yMax
            }))
        }

        // 낙상 데이터 전용 처리 함수 - 연속적인 낙상 이벤트를 그룹화
        const selectFallData = (dailyData?: IBiometricChartItem[], realtimeData?: IBiometricChartItem[]) => {
            const sourceData = viewMode === 'realtime' ? (realtimeData || []) : (dailyData || [])
            const filteredData = sourceData.filter(item => 
                !isNaN(item.x) && item.x > 0 && 
                item.y != null && !isNaN(item.y)
            ).sort((a, b) => a.x - b.x)

            if (filteredData.length === 0) return []

            // 연속적인 낙상 이벤트를 그룹화 (10초 이내의 연속 이벤트는 하나로 처리)
            const groupedFalls: IBiometricChartItem[] = []
            const GROUPING_THRESHOLD = 10 // 10초
            
            let currentGroup: IBiometricChartItem[] = []
            
            for (let i = 0; i < filteredData.length; i++) {
                const currentItem = filteredData[i]
                
                if (currentGroup.length === 0) {
                    // 첫 번째 아이템이면 그룹에 추가
                    currentGroup.push(currentItem)
                } else {
                    const lastItem = currentGroup[currentGroup.length - 1]
                    const timeDiff = currentItem.x - lastItem.x // 초 단위
                    
                    if (timeDiff <= GROUPING_THRESHOLD) {
                        // 10초 이내면 같은 그룹에 추가
                        currentGroup.push(currentItem)
                    } else {
                        // 10초 초과면 이전 그룹을 완료하고 새 그룹 시작
                        if (currentGroup.length > 0) {
                            // 그룹의 첫 번째 시간을 대표 시간으로 사용하고, y값은 합계
                            const groupSum = currentGroup.reduce((sum, item) => sum + (item.y || 0), 0)
                            groupedFalls.push({
                                x: currentGroup[0].x,
                                y: groupSum,
                                yMin: currentGroup[0].yMin,
                                yMax: currentGroup[0].yMax
                            })
                        }
                        currentGroup = [currentItem]
                    }
                }
            }
            
            // 마지막 그룹 처리
            if (currentGroup.length > 0) {
                const groupSum = currentGroup.reduce((sum, item) => sum + (item.y || 0), 0)
                groupedFalls.push({
                    x: currentGroup[0].x,
                    y: groupSum,
                    yMin: currentGroup[0].yMin,
                    yMax: currentGroup[0].yMax
                })
            }

            return groupedFalls.map(item => ({
                x: item.x * 1000, // Chart.js는 밀리초 단위
                y: item.y,
                yMin: item.yMin,
                yMax: item.yMax
            }))
        }

        // 심박수와 호흡수는 min/max/avg 분리
        const selectVitalData = (dailyData?: IBiometricChartItem[], realtimeData?: IBiometricChartItem[]) => {
            const sourceData = viewMode === 'realtime' ? (realtimeData || []) : (dailyData || [])
            const filteredData = sourceData.filter(item => 
                !isNaN(item.x) && item.x > 0 && 
                item.y != null && !isNaN(item.y)
            ).sort((a, b) => a.x - b.x)

            // 각 데이터 포인트에서 min/avg/max 값들을 논리적 순서로 정렬
            const normalizedData = filteredData.map(item => {
                let min = item.yMin ?? item.y;
                let avg = item.y;
                let max = item.yMax ?? item.y;
                
                // NaN 값 처리
                if (isNaN(min)) min = avg;
                if (isNaN(max)) max = avg;
                if (isNaN(avg)) avg = 0;
                
                // 논리적 순서 보장: min <= avg <= max
                const values = [min, avg, max].sort((a, b) => a - b);
                
                return {
                    x: item.x * 1000,
                    sortedMin: values[0],
                    sortedAvg: values[1], 
                    sortedMax: values[2]
                };
            });

            return {
                max: normalizedData.map(item => ({
                    x: item.x,
                    y: item.sortedMax
                })),
                avg: normalizedData.map(item => ({
                    x: item.x,
                    y: item.sortedAvg
                })),
                min: normalizedData.map(item => ({
                    x: item.x,
                    y: item.sortedMin
                }))
            }
        }

        return {
            heartRate: selectVitalData(heartRateData, realtimeHeartRate),
            respiration: selectVitalData(respirationData, realtimeRespiration),
            steps: selectData(stepData, realtimeSteps),
            falls: selectFallData(fallData, realtimeFalls)
        }
    }, [
        heartRateData, respirationData, stepData, fallData,
        realtimeHeartRate, realtimeRespiration, realtimeSteps, realtimeFalls,
        viewMode
    ])

    // 차트 동기화 함수
    const syncAllCharts = useCallback((newZoom: { min?: number, max?: number }) => {
        setSyncedZoom(newZoom)
        
        const charts = [
            heartRateChartRef.current,
            respirationChartRef.current,
            stepChartRef.current,
            fallChartRef.current
        ]

        charts.forEach(chart => {
            if (chart && chart.options.scales?.x) {
                chart.options.scales.x.min = newZoom.min
                chart.options.scales.x.max = newZoom.max
                chart.update('none')
            }
        })
    }, [])

    // 차트 라벨 추출 함수 (HTML 태그 제거 및 다국어 지원)
    const extractChartLabel = useCallback((htmlContent: string) => {
        if (!htmlContent) return '';
        const match = htmlContent.match(/<span[^>]*>([\s\S]*?)<\/span>/);
        if (match && match[1]) {
            return match[1].replace(/[\x00-\x1F\x7F]/g, "");
        }
        return htmlContent.replace(/<[^>]+>/g, '').trim();
    }, []);

    // 공통 차트 옵션 생성 함수
    const createChartOptions = useCallback((
        title: string, 
        color: string, 
        yAxisTitle: string,
        suggestedMin?: number,
        suggestedMax?: number
    ) => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index' as InteractionMode,
            intersect: false,
        },
        plugins: {
            title: {
                display: true,
                text: title,
                color: color,
                font: {
                    size: 16,
                    weight: 'bold' as const
                }
            },
            legend: {
                display: title.includes(t('users.dailyChart.line.max')) || title.includes(t('users.dailyChart.line.average')) || title.includes(t('users.dailyChart.line.min')), // 심박수, 호흡수만 범례 표시
                position: 'top' as const,
                labels: {
                    usePointStyle: true,
                    padding: 15
                }
            },
            tooltip: {
                mode: 'index' as InteractionMode,
                intersect: false,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: 'rgb(75, 85, 99)',
                bodyColor: 'rgb(75, 85, 99)',
                borderColor: 'rgb(229, 231, 235)',
                borderWidth: 1,
                cornerRadius: 8,
                titleAlign: 'center' as const,
                bodyAlign: 'center' as const,
                titleFont: {
                    size: 12,
                    weight: 'normal' as const
                },
                bodyFont: {
                    size: 12,
                    weight: 'normal' as const
                },
                padding: 12,
                callbacks: {
                    title: (tooltipItems: any[]) => {
                        if (tooltipItems.length > 0) {
                            const date = new Date(tooltipItems[0].parsed.x)
                            return viewMode === 'realtime' 
                                ? format(date, 'HH:mm:ss')
                                : format(date, 'MM/dd')
                        }
                        return ''
                    },
                    label: (context: any) => {
                        // 기본 툴팁 형태로 단순화
                        const value = Math.floor(context.parsed.y || 0);
                        return `${context.dataset.label}: ${value}`;
                    }
                }
            },
            zoom: {
                pan: {
                    enabled: true,
                    mode: 'x' as const,
                    onPan: (context: any) => {
                        const { min, max } = context.chart.scales.x
                        syncAllCharts({ min, max })
                    }
                },
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x' as const,
                    onZoom: (context: any) => {
                        const { min, max } = context.chart.scales.x
                        syncAllCharts({ min, max })
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'time' as const,
                time: {
                    displayFormats: {
                        minute: viewMode === 'realtime' ? 'HH:mm' : 'MM/dd',
                        hour: viewMode === 'realtime' ? 'HH:mm' : 'MM/dd',
                        day: 'MM/dd'
                    }
                },
                title: {
                    display: true,
                    text: viewMode === 'realtime' ? t('users.dailyChart.label.realtime') : t('users.dailyChart.label.daily')
                },
                min: syncedZoom.min,
                max: syncedZoom.max
            },
            y: {
                title: {
                    display: true,
                    text: yAxisTitle
                },
                suggestedMin,
                suggestedMax
            }
        }
    }), [syncedZoom, viewMode, syncAllCharts])

    // 심박수/호흡수용 3라인 차트 데이터 생성 함수
    const createVitalChartData = useCallback((
        data: { max: { x: number, y: number }[], avg: { x: number, y: number }[], min: { x: number, y: number }[] }, 
        baseColor: string
    ) => ({
        datasets: [
            {
                label: t('users.dailyChart.line.max'),
                data: data.max,
                borderColor: baseColor,
                backgroundColor: baseColor + '20',
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4
            },
            {
                label: t('users.dailyChart.line.average'),
                data: data.avg,
                borderColor: baseColor + 'CC', // 약간 투명하게
                backgroundColor: baseColor + '15',
                borderWidth: 3, // 두껍게
                fill: false,
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4
            },
            {
                label: t('users.dailyChart.line.min'),
                data: data.min,
                borderColor: baseColor + '99', // 더 투명하게
                backgroundColor: baseColor + '10',
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4
            }
        ]
    }), [])

    // 일반 차트 데이터 생성 함수 (걸음수, 낙상)
    const createChartData = useCallback((data: { x: number, y: number }[], color: string, label: string) => ({
        datasets: [
            {
                label,
                data,
                borderColor: color,
                backgroundColor: color + '20',
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4
            }
        ]
    }), [])

    // 통계 계산
    const statistics = useMemo(() => {
        const calcStats = (data: { x: number, y: number }[]) => {
            if (data.length === 0) return null
            const values = data.map(d => d.y).filter(v => !isNaN(v) && isFinite(v))
            if (values.length === 0) return null
            
            return {
                latest: values[values.length - 1] || 0,
                average: values.reduce((sum, v) => sum + v, 0) / values.length,
                max: Math.max(...values),
                min: Math.min(...values)
            }
        }

        const calcVitalStats = (data: { max: { x: number, y: number }[], avg: { x: number, y: number }[], min: { x: number, y: number }[] }) => {
            if (data.avg.length === 0) return null
            const avgValues = data.avg.map(d => d.y).filter(v => !isNaN(v) && isFinite(v))
            if (avgValues.length === 0) return null
            
            return {
                latest: avgValues[avgValues.length - 1] || 0,
                average: avgValues.reduce((sum, v) => sum + v, 0) / avgValues.length,
                max: Math.max(...data.max.map(d => d.y).filter(v => !isNaN(v) && isFinite(v))),
                min: Math.min(...data.min.map(d => d.y).filter(v => !isNaN(v) && isFinite(v)))
            }
        }

        // 낙상 전용 통계 계산 - 그룹화된 데이터의 특성을 고려
        const calcFallStats = (data: { x: number, y: number }[]) => {
            if (data.length === 0) return null
            const values = data.map(d => d.y).filter(v => !isNaN(v) && isFinite(v))
            if (values.length === 0) return null
            
            return {
                totalCount: values.reduce((sum, v) => sum + v, 0), // 전체 낙상 횟수 (모든 그룹의 합계)
                groupCount: data.length, // 그룹화된 낙상 이벤트 수
                latest: values[values.length - 1] || 0, // 최근 그룹의 낙상 횟수
                max: Math.max(...values), // 단일 그룹에서 최대 낙상 횟수
                min: Math.min(...values)  // 단일 그룹에서 최소 낙상 횟수
            }
        }

        return {
            heartRate: calcVitalStats(processedData.heartRate),
            respiration: calcVitalStats(processedData.respiration),
            steps: calcStats(processedData.steps),
            falls: calcFallStats(processedData.falls)
        }
    }, [processedData])

    // 줌 리셋 함수
    const resetZoom = useCallback(() => {
        setSyncedZoom({})
        const charts = [
            heartRateChartRef.current,
            respirationChartRef.current,
            stepChartRef.current,
            fallChartRef.current
        ]

        charts.forEach(chart => {
            if (chart && (chart as any).resetZoom) {
                (chart as any).resetZoom()
            }
        })
    }, [])

    return (
        <div className={`${className} biometric-dashboard`}>
            {/* 헤더 */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <span className="text-3xl">📊</span>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">{t('V3.biometricDashboard.title')}</h2>
                            <p className="text-gray-600">{t('V3.biometricDashboard.description')}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* 줌 리셋 버튼 */}
                        <button
                            onClick={resetZoom}
                            className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                        >
                            🔍 {t('common.zoomOut')}
                        </button>

                        {/* 데이터 타입 선택 탭 */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('realtime')}
                                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                                    viewMode === 'realtime' 
                                        ? 'bg-blue-500 text-white shadow-sm' 
                                        : 'text-gray-600 hover:text-blue-600'
                                }`}
                            >
                                {t('common.realtimeView')} (10분)
                            </button>
                            <button
                                onClick={() => setViewMode('daily')}
                                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                                    viewMode === 'daily' 
                                        ? 'bg-blue-500 text-white shadow-sm' 
                                        : 'text-gray-600 hover:text-blue-600'
                                }`}
                            >
                                {t('common.dayView')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* 통계 카드 */}
                <div className="grid grid-cols-4 gap-4">
                    {/* 심박수 */}
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-red-500">❤️</span>
                            <h3 className="font-semibold text-red-700">{parse(t('users.detail.heartRate.title'))}</h3>
                        </div>
                        {statistics.heartRate && (
                            <div className="space-y-1 text-sm">
                                <p><span className="font-medium">{t('V3.biometricDashboard.stats.current')}:</span> {Math.round(statistics.heartRate.latest)} bpm</p>
                                <p><span className="font-medium">{t('users.dailyChart.line.average')}:</span> {Math.round(statistics.heartRate.average)} bpm</p>
                            </div>
                        )}
                    </div>

                    {/* 호흡수 */}
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-blue-500">🫁</span>
                            <h3 className="font-semibold text-blue-700">{parse(t('users.detail.respiration.title'))}</h3>
                        </div>
                        {statistics.respiration && (
                            <div className="space-y-1 text-sm">
                                <p><span className="font-medium">{t('V3.biometricDashboard.stats.current')}:</span> {Math.round(statistics.respiration.latest)} rpm</p>
                                <p><span className="font-medium">{t('users.dailyChart.line.average')}:</span> {Math.round(statistics.respiration.average)} rpm</p>
                            </div>
                        )}
                    </div>

                    {/* 걸음수 */}
                    <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-green-500">🚶</span>
                            <h3 className="font-semibold text-green-700">{parse(t('users.detail.step.title'))}</h3>
                        </div>
                        {statistics.steps && (
                            <div className="space-y-1 text-sm">
                                <p><span className="font-medium">{t('V3.biometricDashboard.stats.current')}:</span> {Math.round(statistics.steps.latest).toLocaleString()} {t('common.step')}</p>
                                <p><span className="font-medium">{t('users.dailyChart.line.average')}:</span> {Math.round(statistics.steps.average).toLocaleString()} {t('common.step')}</p>
                            </div>
                        )}
                    </div>

                    {/* 낙상 */}
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-yellow-500">⚠️</span>
                            <h3 className="font-semibold text-yellow-700">{t('notification.falldetection.title')}</h3>
                        </div>
                        {statistics.falls && (
                            <div className="space-y-1 text-sm">
                                <p><span className="font-medium">{t('V3.biometricDashboard.stats.total')}:</span> {statistics.falls.totalCount} {t('common.count')}</p>
                                <p><span className="font-medium">{t('V3.biometricDashboard.stats.events')}:</span> {statistics.falls.groupCount} {t('V3.biometricDashboard.stats.cases')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 동기화된 차트들 */}
            <div className="grid grid-cols-1 gap-6">
                {/* 심박수 차트 */}
                <div className="bg-white rounded-lg shadow-lg p-4">
                    <div className="h-80">
                        <Line
                            ref={heartRateChartRef}
                            data={createVitalChartData(processedData.heartRate, '#ef4444')}
                            options={createChartOptions(`❤️ ${t('V3.biometricDashboard.chart.heartRate.title')} (${t('users.dailyChart.line.max')}/${t('users.dailyChart.line.average')}/${t('users.dailyChart.line.min')})`, '#ef4444', `${extractChartLabel(t('users.detail.heartRate.title'))} (bpm)`, 40, 160)}
                        />
                    </div>
                </div>

                {/* 호흡수 차트 */}
                <div className="bg-white rounded-lg shadow-lg p-4">
                    <div className="h-80">
                        <Line
                            ref={respirationChartRef}
                            data={createVitalChartData(processedData.respiration, '#3b82f6')}
                            options={createChartOptions(`🫁 ${t('V3.biometricDashboard.chart.respiration.title')} (${t('users.dailyChart.line.max')}/${t('users.dailyChart.line.average')}/${t('users.dailyChart.line.min')})`, '#3b82f6', `${extractChartLabel(t('users.detail.respiration.title'))} (rpm)`, 8, 30)}
                        />
                    </div>
                </div>

                {/* 걸음수 차트 */}
                <div className="bg-white rounded-lg shadow-lg p-4">
                    <div className="h-80">
                        <Line
                            ref={stepChartRef}
                            data={createChartData(processedData.steps, '#10b981', extractChartLabel(t('users.detail.step.title')))}
                            options={createChartOptions(`🚶 ${t('V3.biometricDashboard.chart.steps.title')}`, '#10b981', extractChartLabel(t('users.detail.step.title')), 0)}
                        />
                    </div>
                </div>

                {/* 낙상 감지 차트 */}
                <div className="bg-white rounded-lg shadow-lg p-4">
                    <div className="h-80">
                        <Line
                            ref={fallChartRef}
                            data={createChartData(processedData.falls, '#f59e0b', t('V3.biometricDashboard.chart.falls.label'))}
                            options={createChartOptions(`⚠️ ${t('notification.falldetection.title')}`, '#f59e0b', t('V3.biometricDashboard.chart.falls.label'), 0)}
                        />
                    </div>
                </div>
            </div>

            {/* 사용법 안내 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <h4 className="font-semibold text-blue-800 mb-2">📌 {t('V3.biometricDashboard.guide.title')}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm text-blue-700">
                    <div>
                        <p>• <strong>{t('V3.biometricDashboard.guide.sync.title')}:</strong> {t('V3.biometricDashboard.guide.sync.description')}</p>
                        <p>• <strong>{t('V3.biometricDashboard.guide.zoom.title')}:</strong> {t('V3.biometricDashboard.guide.zoom.description')}</p>
                    </div>
                    <div>
                        <p>• <strong>{t('V3.biometricDashboard.guide.pan.title')}:</strong> {t('V3.biometricDashboard.guide.pan.description')}</p>
                        <p>• <strong>{t('V3.biometricDashboard.guide.reset.title')}:</strong> {t('V3.biometricDashboard.guide.reset.description')}</p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default BiometricDashboard 